import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";

const API_URL = process.env.REACT_APP_BASE_URL;
const TOKEN = "a94c7ac0083ae190f0fe61143a7547799b5334c4";

const headers = {
  Authorization: `Token ${TOKEN}`,
  "Content-Type": "application/json",
};

export const fetchUnits = createAsyncThunk(
  "unit/fetchUnits",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

     if (filters && filters.length > 0) {
        filters.forEach(
          ({
            filterField,
            filterValue,
          }: {
            filterField: string;
            filterValue: any;
          }) => {
            if (
              filterField &&
              filterValue !== undefined &&
              filterValue !== null
            ) {
              // Handle date range filters
              if (typeof filterValue === "object" && filterValue !== null) {
                if (filterField === "created_at") {
                  if (filterValue.from_date) {
                    params.append("created_at_from", filterValue.from_date);
                  }
                  if (filterValue.to_date) {
                    params.append("created_at_to", filterValue.to_date);
                  }
                } else if (filterField === "updated_at") {
                  if (filterValue.from_date) {
                    params.append("updated_at_from", filterValue.from_date);
                  }
                  if (filterValue.to_date) {
                    params.append("updated_at_to", filterValue.to_date);
                  }
                } else {
                  // Handle other object filters
                  Object.entries(filterValue).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && value !== "") {
                      params.append(key, String(value));
                    }
                  });
                }
              } else {
                // Handle primitive values (string, number)
                params.append(filterField, filterValue);
              }
            }
          }
        );
      }
    const response: any = await Axios.get(`/master/unit/?${params.toString()}`);
    return {
      data: response.data.data || [],
      rowCount: response.data.count || 0,
    };
  }
);

export const fetchUnitById = createAsyncThunk(
  "unit/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(`/master/unit/${id}/get/`);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Unit by ID"
      );
    }
  }
);

export const fetchUOMByDropDown = createAsyncThunk(
  "UOM/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/master/UOM/dropdown/`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch UOM list");
    }
  }
);

// Fetch units dropdown for forms (non-master endpoint)
export const fetchUnitDropdownForForms = createAsyncThunk(
  "UOM/fetchUnitDropdownForForms",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/unit/dropdown/uom_name/`);
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.uom_name || [];
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch Unit dropdown");
    }
  }
);

export const updateUnit = createAsyncThunk(
  "uom/updateUnit",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(`/master/unit/${data.id}/update/`, data);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update Unit");
    }
  }
);

export const updateStatusInUnit = createAsyncThunk(
  "uom/updateStatusInUnit",
  async (data: any) => {
    const payload = { is_active: data.is_active };
    const response: any = await Axios.patch(
      `/master/unit/update-status/${data.id}/`,
      payload
    );
    return response;
  }
);

export const addUnit = createAsyncThunk(
  "unit/addUnit",
  async (data: any, { rejectWithValue }) => {
    const payload = {
      unit_name: data.unit_name,
      unit_symbol: data.unit_symbol,
    };
    try {
      const response: any = await Axios.post(`/master/unit/create/`, payload);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add Unit");
    }
  }
);

export const deleteUnit = createAsyncThunk(
  "uom/delete",
  async (id: number, { rejectWithValue }) => {
    try {
      await Axios.delete(`/master/uom/${id}/`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// export const exportUnitCSV = async () => {
//   const response = await Axios.get("/unit/?export=csv", {
//     responseType: "blob",
//   });

//   return {
//     blob: response.data,
//     filename:
//       response.headers["content-disposition"]
//         ?.split("filename=")[1]
//         ?.replace(/"/g, "") || "unit.csv",
//   };
// };

export const exportUnitCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();

  params.append("export", "excel");

  if (search) params.append("search", search);

  if (filters && filters.length > 0) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (filterField && filterValue !== undefined && filterValue !== null) {
          if (typeof filterValue === "object" && filterValue !== null) {
            // Handle date filters
            if (
              (filterValue.from_date || filterValue.to_date) &&
              (filterField === "created_at" || filterField === "updated_at")
            ) {
              if (filterField === "created_at") {
                if (filterValue.from_date) {
                  params.append("created_at_from", String(filterValue.from_date));
                }
                if (filterValue.to_date) {
                  params.append("created_at_to", String(filterValue.to_date));
                }
              } else if (filterField === "updated_at") {
                if (filterValue.from_date) {
                  params.append("updated_at_from", String(filterValue.from_date));
                }
                if (filterValue.to_date) {
                  params.append("updated_at_to", String(filterValue.to_date));
                }
              }
            } else {
              // Handle other object filters
              Object.entries(filterValue).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                  params.append(key, String(value));
                }
              });
            }
          } else {
            params.append(filterField, filterValue);
          }
        }
      }
    );
  }

  const response = await Axios.get(`/master/unit/?${params.toString()}`, {
    responseType: "blob",
  });

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "unit.csv",
  };
};
